// What the plugin keeps in its folder inside a game, `puzzles/`:
// - `puzzles.json`: the derived list, with the finder version that made it.
//   Regenerated only when that version changes; never written into the scan.
// - `attempts.json`: the reader's record, keyed by puzzle id, untouched by
//   regeneration. Not regenerable, so never mixed with the list.
import type { Storage } from '@chess/api-storage';
import { FINDER_VERSION, type Puzzle } from './finder.ts';

export const PUZZLES_FILE = 'puzzles.json';
export const ATTEMPTS_FILE = 'attempts.json';

export interface PuzzleList {
  schemaVersion: 1;
  finderVersion: number;
  gameId: string;
  /** The scan's `updatedAt` the list was made from. */
  scanUpdatedAt: string;
  puzzles: Puzzle[];
}

export type AttemptStatus = 'solved' | 'failed';

export interface Attempt {
  status: AttemptStatus;
  /** How many tries it took, this session of attempts included. */
  tries: number;
  /** ISO 8601 of the last outcome. */
  at: string;
}

export interface Attempts {
  schemaVersion: 1;
  attempts: Record<string, Attempt>;
}

/** The stored list if it was made by this finder, else undefined. */
export async function readPuzzles(folder: Storage): Promise<PuzzleList | undefined> {
  const text = await folder.readText(PUZZLES_FILE);
  if (!text) return undefined;
  try {
    const value = JSON.parse(text) as Partial<PuzzleList>;
    if (value.schemaVersion !== 1 || value.finderVersion !== FINDER_VERSION || !Array.isArray(value.puzzles)) return undefined;
    return value as PuzzleList;
  } catch {
    return undefined;
  }
}

export async function writePuzzles(folder: Storage, list: PuzzleList): Promise<void> {
  await folder.write(PUZZLES_FILE, JSON.stringify(list));
}

export async function readAttempts(folder: Storage): Promise<Attempts> {
  const text = await folder.readText(ATTEMPTS_FILE);
  if (!text) return { schemaVersion: 1, attempts: {} };
  try {
    const value = JSON.parse(text) as Partial<Attempts>;
    if (value.schemaVersion !== 1 || typeof value.attempts !== 'object' || !value.attempts) return { schemaVersion: 1, attempts: {} };
    return value as Attempts;
  } catch {
    return { schemaVersion: 1, attempts: {} };
  }
}

/** Records an outcome for a puzzle, replacing the last; tries accumulate. */
export async function recordAttempt(folder: Storage, puzzleId: string, status: AttemptStatus, tries: number): Promise<Attempts> {
  const all = await readAttempts(folder);
  all.attempts[puzzleId] = { status, tries, at: new Date().toISOString() };
  await folder.write(ATTEMPTS_FILE, JSON.stringify(all));
  return all;
}
